import ticketService from '../../services/tickets/ticket.service.js'

import entityUtility from '../../utils/entity.utility.js'
import httpUtility from '../../utils/http.utility.js'

import { useToast } from '../../composables/useToast.js'

import { t } from '@/i18n'

const toast = useToast()

async function getTicket(id) {
  try {
    return await ticketService.get.one(id)
  } catch (err) {
    toast.error(httpUtility.error.message(err, t('tickets.management.errors.loadTickets')))
    return null
  }
}

async function getActiveTickets(filters = {}) {
  try {
    const result = await ticketService.get.active(filters)
    return { ...result, settingsHosts: result.settingsHosts ?? {} }
  } catch (err) {
    toast.error(httpUtility.error.message(err, t('tickets.management.errors.loadTickets')))
    return null
  }
}

async function getArchivedTickets(filters = {}) {
  try {
    const result = await ticketService.get.archived(filters)
    return { ...result, settingsHosts: result.settingsHosts ?? {} }
  } catch (err) {
    toast.error(httpUtility.error.message(err, t('tickets.management.errors.loadTickets')))
    return null
  }
}

async function saveTicket(payload) {
  try {
    let response
    if (payload._id) response = await ticketService.update.ticket(payload._id, payload)
    else response = await ticketService.create.ticket(payload)
    return response.data.ticket
  } catch (err) {
    toast.error(httpUtility.error.message(err, t('tickets.management.errors.saveTicket')))
    return null
  }
}

async function saveTicketComment(id, body) {
  try {
    const response = await ticketService.create.comment(id, body)
    return response.data.ticket
  } catch (err) {
    toast.error(httpUtility.error.message(err, t('tickets.management.errors.saveTicket')))
    return null
  }
}

async function saveTicketAttachments(id, files) {
  try {
    const formData = new FormData()
    for (const file of files) {
      // Encode names so non-ASCII filenames survive the multipart headers.
      formData.append('files', file, encodeURIComponent(file.name))
    }
    const response = await ticketService.create.attachments(id, formData)
    return response.data.ticket
  } catch (err) {
    toast.error(httpUtility.error.message(err, t('tickets.management.errors.saveTicket')))
    return null
  }
}

async function updateTicketStatus(id, status) {
  try {
    const response = await ticketService.update.status(id, status)
    return response.data.ticket
  } catch (err) {
    toast.error(httpUtility.error.message(err, t('tickets.management.errors.updateStatus')))
    return null
  }
}

async function updateTicketDueDate(id, dueDate) {
  try {
    const response = await ticketService.update.ticket(id, { dueDate })
    return response.data.ticket
  } catch (err) {
    toast.error(httpUtility.error.message(err, t('tickets.management.errors.updateDueDate')))
    return null
  }
}

async function archiveTicket(id) {
  try {
    await ticketService.archive.ticket(id)
    return true
  } catch (err) {
    toast.error(httpUtility.error.message(err, t('tickets.management.errors.archiveTicket')))
    return false
  }
}

async function archiveTickets(ids) {
  try {
    await ticketService.archive.tickets(ids)
    return true
  } catch (err) {
    toast.error(httpUtility.error.message(err, t('tickets.management.errors.bulkArchive')))
    return false
  }
}

async function restoreTicket(id) {
  try {
    await ticketService.restore.ticket(id)
    return true
  } catch (err) {
    toast.error(httpUtility.error.message(err, t('tickets.management.errors.restoreTicket')))
    return false
  }
}

async function deleteTicket(id) {
  try {
    await ticketService.delete.ticket(id)
    return true
  } catch (err) {
    toast.error(httpUtility.error.message(err, t('tickets.management.errors.deleteTicket')))
    return false
  }
}

async function deleteTickets(ids) {
  try {
    await ticketService.delete.tickets(ids)
    return true
  } catch (err) {
    toast.error(httpUtility.error.message(err, t('tickets.management.errors.bulkDelete')))
    return false
  }
}

async function deleteTicketAttachment(id, attachmentId) {
  try {
    const response = await ticketService.delete.attachment(id, attachmentId)
    return response.data.ticket
  } catch (err) {
    toast.error(httpUtility.error.message(err, t('tickets.management.errors.saveTicket')))
    return null
  }
}

class TicketController {
  get = {
    /**
     * Load one ticket by id. Returns the ticket document on success,
     * `null` on failure (toasts the error).
     *
     * @param {string} id
     * @returns {Promise<object|null>}
     */
    async one(id) {
      return getTicket(id)
    },

    /**
     * Load active tickets matching optional filters. Returns
     * `{ tickets, totalLength }` on success, `null` on failure (toasts
     * the error).
     *
     * @param {object} [filters={}]
     * @returns {Promise<{ tickets: object[], totalLength: number }|null>}
     */
    async active(filters = {}) {
      return getActiveTickets(filters)
    },

    /**
     * Load archived tickets matching optional filters. Returns
     * `{ tickets, totalLength }` on success, `null` on failure (toasts
     * the error).
     *
     * @param {object} [filters={}]
     * @returns {Promise<{ tickets: object[], totalLength: number }|null>}
     */
    async archived(filters = {}) {
      return getArchivedTickets(filters)
    },
  }

  /**
   * Look up one ticket by `id` in `array`. Returns the row, or `null`
   * when `id` is missing or not found.
   *
   * @param {string|null|undefined} id
   * @param {Array<{ _id: string }>} array
   * @returns {object|null}
   */
  find(id, array) {
    return entityUtility.find(id, array)
  }

  save = {
    /**
     * Create or update a ticket. Branches on `payload._id` (truthy →
     * update; omitted → create). Returns the saved ticket on success,
     * `null` on failure (toasts the error).
     *
     * @param {object} payload
     * @returns {Promise<object|null>}
     */
    async ticket(payload) {
      return saveTicket(payload)
    },

    /**
     * Add a text comment to a ticket. Returns the updated ticket on
     * success, `null` on failure (toasts the error).
     *
     * @param {string} id
     * @param {{ comment: string }} body
     * @returns {Promise<object|null>}
     */
    async comment(id, body) {
      return saveTicketComment(id, body)
    },

    /**
     * Attach files to a ticket. Returns the updated ticket on success,
     * `null` on failure (toasts the error).
     *
     * @param {string} id
     * @param {File[]} files
     * @returns {Promise<object|null>}
     */
    async attachments(id, files) {
      return saveTicketAttachments(id, files)
    },
  }

  update = {
    /**
     * Change a ticket's workflow status. Returns the saved ticket on success,
     * `null` on failure (toasts the error).
     *
     * @param {string} id
     * @param {string} status
     * @returns {Promise<object|null>}
     */
    async status(id, status) {
      return updateTicketStatus(id, status)
    },

    /**
     * Change a ticket's due date. Returns the saved ticket on success,
     * `null` on failure (toasts the error).
     *
     * @param {string} id
     * @param {string|null} dueDate `yyyy-MM-dd` or null
     * @returns {Promise<object|null>}
     */
    async dueDate(id, dueDate) {
      return updateTicketDueDate(id, dueDate)
    },
  }

  archive = {
    /**
     * Archive one ticket. Returns `true` on success, `false` on failure
     * (toasts the error).
     *
     * @param {string} id
     * @returns {Promise<boolean>}
     */
    async ticket(id) {
      return archiveTicket(id)
    },

    /**
     * Archive many tickets in one request. Returns `true` on success,
     * `false` on failure (toasts the error).
     *
     * @param {string[]} ids
     * @returns {Promise<boolean>}
     */
    async tickets(ids) {
      return archiveTickets(ids)
    },
  }

  restore = {
    /**
     * Restore one archived ticket. Returns `true` on success, `false`
     * on failure (toasts the error).
     *
     * @param {string} id
     * @returns {Promise<boolean>}
     */
    async ticket(id) {
      return restoreTicket(id)
    },
  }

  delete = {
    /**
     * Soft-delete one ticket. Returns `true` on success, `false` on
     * failure (toasts the error).
     *
     * @param {string} id
     * @returns {Promise<boolean>}
     */
    async ticket(id) {
      return deleteTicket(id)
    },

    /**
     * Soft-delete many tickets in one request. Returns `true` on
     * success, `false` on failure (toasts the error).
     *
     * @param {string[]} ids
     * @returns {Promise<boolean>}
     */
    async tickets(ids) {
      return deleteTickets(ids)
    },

    /**
     * Remove one attachment from a ticket. Returns the updated ticket
     * on success, `null` on failure (toasts the error).
     *
     * @param {string} id
     * @param {string} attachmentId
     * @returns {Promise<object|null>}
     */
    async attachment(id, attachmentId) {
      return deleteTicketAttachment(id, attachmentId)
    },
  }
}

export default new TicketController()
